import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageMeta from '@/components/feature/PageMeta';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getLatestUpdates } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const comic = getComicById(id || '');
  const relatedComics = getLatestUpdates().filter(c => c.id !== id).slice(0, 4);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex flex-col items-center justify-center px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-2xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 font-heading">漫画未找到</h1>
          <p className="mt-2 text-sm text-foreground-500">该漫画不存在或已被移除</p>
          <Link
            to="/"
            className="mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `https://hanmanzhijia.com/article/${comic.id}`,
    image: comic.cover,
    genre: comic.tags,
    dateModified: comic.updatedAt,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'https://hanmanzhijia.com',
    },
  };

  return (
    <>
      <PageMeta
        title={`${comic.title} - ${comic.author} - 韩漫之家 免费韩国漫画在线阅读`}
        description={`${comic.title} - ${comic.description.substring(0, 120)}，作者${comic.author}，评分${comic.rating}，${comic.status}，尽在韩漫之家免费在线阅读。`}
        keywords={`${comic.title},${comic.author},${comic.categoryName},韩国漫画,韩漫之家,免费漫画`}
        canonicalUrl={`https://hanmanzhijia.com/article/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
        schema={schema}
      />
      <ScrollToTopButton />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comic.categoryName, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <article className="py-6">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-60">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - ${comic.author}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {comic.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-500">
                  作者：<strong className="text-foreground-700 font-medium">{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className="inline-flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    <strong className="text-foreground-900 font-semibold">{comic.rating}</strong>
                  </span>
                  <span className="text-sm text-foreground-500">
                    {formatViews(comic.views)} 阅读
                  </span>
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-label font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-800 font-label">
                    {comic.categoryName}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mt-4">
                  {comic.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-[10px] px-2 py-0.5 rounded bg-background-100 text-foreground-600 border border-background-200/60 font-label"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <h2 className="text-sm font-semibold font-heading text-foreground-900 mb-2">
                    作品简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/category/${comic.category}`}
                    className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <Link
                    to="/"
                    className="px-5 py-2.5 border border-background-200 hover:border-primary-300 text-foreground-700 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加入收藏
                  </Link>
                </div>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="py-8 border-t border-background-200" aria-labelledby="chapters-title">
            <div className="flex items-center justify-between mb-6">
              <h2 id="chapters-title" className="text-lg font-bold font-heading text-foreground-950">
                章节列表
              </h2>
              <span className="text-xs text-foreground-500">
                共 {comic.chapters.length} 话
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {comic.chapters.map(ch => (
                <a
                  key={ch.id}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                    {ch.title}
                  </span>
                  <span className="text-xs text-foreground-400">
                    第{ch.number}话
                  </span>
                </a>
              ))}
            </div>
          </section>

          {/* Related */}
          <section className="py-8 border-t border-background-200" aria-labelledby="related-title">
            <div className="flex items-center justify-between mb-6">
              <h2 id="related-title" className="text-lg font-bold font-heading text-foreground-950">
                相关推荐
              </h2>
              <Link
                to="/"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看更多
                <i className="ri-arrow-right-line ml-1 w-4 h-4 inline-flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop>
              {relatedComics.map(c => (
                <ComicCard
                  key={c.id}
                  id={c.id}
                  title={c.title}
                  author={c.author}
                  cover={c.cover}
                  rating={c.rating}
                  views={c.views}
                  tags={c.tags}
                  status={c.status}
                  categoryName={c.categoryName}
                />
              ))}
            </div>
          </section>

          {/* Back Navigation */}
          <div className="py-8 text-center">
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}